import { formatSecondsToHms, uniqueRefOptions } from './qstat-calls.model';
import { normalizePhoneUsersResponse } from './phone-users.model';
import { normalizeQstatDashboardAnalytics } from './qstat-dashboard.model';
import { addCalendarDaysInAppTimezone, endOfDayInAppTimezone, getDatePartsInAppTimezone, getWeekdayInAppTimezone, lastDayOfMonthInAppTimezone, startOfDayInAppTimezone, utcFromAppTimezoneParts, formatTableDate, } from '@/shared/utils/app-timezone';
export function sumCountRows(rows) {
    return rows.reduce((sum, row) => sum + (row.count || 0), 0);
}
export function emptyQueueStatusReport() {
    return {
        totalAnsweredCalls: 0,
        totalUnansweredCalls: 0,
        answeredByAgent: [],
        answeredByQueue: [],
        disconnectionCause: [],
        unansweredByQueue: [],
        distPerDay: [],
        distPerHour: [],
        distPerDayOfWeek: [],
        queuePerformance: [],
        satisfaction: {
            totalRatedCalls: 0,
            overallAverageRating: 0,
            satisfactionByAgent: [],
            ratingDistribution: [],
            detailedCallRecords: [],
        },
    };
}
export function formatPercent(count, total, fractionDigits = 2) {
    if (total <= 0)
        return '';
    return `${((count / total) * 100).toFixed(fractionDigits)}%`;
}
export function formatAnswerRate(answered, unanswered) {
    const total = answered + unanswered;
    if (total <= 0)
        return '';
    return formatPercent(answered, total);
}
export function formatQueueStatusPeriod(date) {
    if (!date)
        return '';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
export function formatDistributionDate(isoDate) {
    return formatTableDate(isoDate, isoDate);
}
export function formatHourLabel(hour) {
    return `${hour}:00`;
}
export function avgTalkForDayOfWeek(row) {
    if (row.answered <= 0)
        return '';
    return formatSecondsToHms(Math.round(row.totalTalkSeconds / row.answered));
}
export function avgHoldForDayOfWeek(row) {
    if (row.answered <= 0)
        return '';
    return formatSecondsToHms(Math.round(row.totalHoldSeconds / row.answered));
}
export function getRatingDisplay(rating) {
    return rating === 1 ? '✓ Satisfied' : '✗ Not Satisfied';
}
export function getRatingClass(rating) {
    return rating === 1 ? 'text-green-600' : 'text-red-600';
}
export function computeIntervalRange(interval) {
    const today = getDatePartsInAppTimezone(new Date());
    const endDate = endOfDayInAppTimezone(today);
    const noon = utcFromAppTimezoneParts({
        year: today.year,
        month: today.month,
        day: today.day,
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
    });
    switch (interval) {
        case 'today':
            return { startDate: startOfDayInAppTimezone(today), endDate };
        case 'thisWeek': {
            const weekday = getWeekdayInAppTimezone(noon);
            const weekStart = addCalendarDaysInAppTimezone(today.year, today.month, today.day, -weekday);
            const weekEnd = addCalendarDaysInAppTimezone(weekStart.year, weekStart.month, weekStart.day, 6);
            return {
                startDate: startOfDayInAppTimezone(weekStart),
                endDate: endOfDayInAppTimezone(weekEnd),
            };
        }
        case 'thisMonth': {
            const monthStart = { year: today.year, month: today.month, day: 1 };
            const monthEnd = {
                year: today.year,
                month: today.month,
                day: lastDayOfMonthInAppTimezone(today.year, today.month),
            };
            return {
                startDate: startOfDayInAppTimezone(monthStart),
                endDate: endOfDayInAppTimezone(monthEnd),
            };
        }
        case 'last3months': {
            const shifted = new Date(Date.UTC(today.year, today.month - 1, today.day));
            shifted.setUTCMonth(shifted.getUTCMonth() - 2);
            shifted.setUTCDate(1);
            const startParts = {
                year: shifted.getUTCFullYear(),
                month: shifted.getUTCMonth() + 1,
                day: shifted.getUTCDate(),
            };
            return { startDate: startOfDayInAppTimezone(startParts), endDate };
        }
        default:
            return { startDate: startOfDayInAppTimezone(today), endDate };
    }
}
function asString(value) {
    return value == null ? '' : String(value);
}
function asNumber(value) {
    if (value == null || value === '')
        return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}
function asNumberOrNull(value) {
    if (value == null || value === '')
        return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}
function countRowLabel(o) {
    return asString(o.label ?? o.agent ?? o.queueName ?? o.cause ?? o.name);
}
function normalizeCountRows(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (Array.isArray(row)) {
            const label = asString(row[0]);
            if (!label)
                return null;
            return { label, count: asNumber(row[1]) };
        }
        if (row && typeof row === 'object') {
            const o = row;
            const label = countRowLabel(o);
            if (!label)
                return null;
            return { label, count: asNumber(o.count) };
        }
        return null;
    })
        .filter((row) => row !== null);
}
export function normalizeQueueCountRows(raw) {
    if (Array.isArray(raw))
        return normalizeCountRows(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeCountRows(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
export function normalizeQueueDistPerDay(raw) {
    if (Array.isArray(raw))
        return normalizeDistPerDay(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeDistPerDay(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
export function normalizeQueueDistPerHour(raw) {
    if (Array.isArray(raw))
        return normalizeDistPerHour(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeDistPerHour(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
export function normalizeQueueDistPerDayOfWeek(raw) {
    if (Array.isArray(raw))
        return normalizeDistPerDayOfWeek(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeDistPerDayOfWeek(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
export function normalizeQueuePerformanceRows(raw) {
    if (Array.isArray(raw))
        return normalizeQueuePerformance(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeQueuePerformance(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
export function normalizeSatisfactionByAgentRows(raw) {
    const rows = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object'
            ? (raw.content ??
                raw.data ??
                raw.rows ??
                raw.items)
            : [];
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (!row || typeof row !== 'object')
            return null;
        const o = row;
        const agent = asString(o.agent ?? o.label ?? o.name);
        if (!agent)
            return null;
        return {
            agent,
            satisfactionPercent: asNumber(o.satisfactionPercent ?? o.percent),
            totalRatedCalls: asNumber(o.totalRatedCalls ?? o.total),
            satisfiedCalls: asNumber(o.satisfiedCalls ?? o.satisfied),
        };
    })
        .filter((row) => row != null);
}
export function normalizeRatingDistributionRows(raw) {
    const rows = Array.isArray(raw)
        ? raw
        : raw && typeof raw === 'object'
            ? (raw.content ??
                raw.data ??
                raw.rows ??
                raw.items)
            : [];
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (!row || typeof row !== 'object')
            return null;
        const o = row;
        return {
            rating: asNumber(o.rating),
            count: asNumber(o.count),
        };
    })
        .filter((row) => row != null);
}
export function normalizeDetailedSatisfactionRows(raw) {
    if (Array.isArray(raw))
        return normalizeDetailedSatisfaction(raw);
    if (raw && typeof raw === 'object') {
        const o = raw;
        return normalizeDetailedSatisfaction(o.content ?? o.data ?? o.rows ?? o.items);
    }
    return [];
}
function normalizeDistPerDay(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (Array.isArray(row)) {
            const date = asString(row[0]);
            if (!date)
                return null;
            return {
                date,
                answered: asNumber(row[1]),
                unanswered: asNumber(row[2]),
            };
        }
        if (row && typeof row === 'object') {
            const o = row;
            const date = asString(o.date);
            if (!date)
                return null;
            return {
                date,
                answered: asNumber(o.answered),
                unanswered: asNumber(o.unanswered),
            };
        }
        return null;
    })
        .filter((row) => row !== null);
}
function normalizeDistPerHour(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (Array.isArray(row)) {
            return {
                hour: asNumber(row[0]),
                answered: asNumber(row[1]),
                unanswered: asNumber(row[2]),
            };
        }
        if (row && typeof row === 'object') {
            const o = row;
            return {
                hour: asNumber(o.hour),
                answered: asNumber(o.answered),
                unanswered: asNumber(o.unanswered),
            };
        }
        return null;
    })
        .filter((row) => row !== null);
}
function normalizeDistPerDayOfWeek(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (Array.isArray(row)) {
            const dayOfWeek = asString(row[0]);
            if (!dayOfWeek)
                return null;
            return {
                dayOfWeek,
                answered: asNumber(row[1]),
                unanswered: asNumber(row[2]),
                totalTalkSeconds: asNumber(row[4]),
                totalHoldSeconds: asNumber(row[5]),
            };
        }
        if (row && typeof row === 'object') {
            const o = row;
            const dayOfWeek = asString(o.dayOfWeek);
            if (!dayOfWeek)
                return null;
            return {
                dayOfWeek,
                answered: asNumber(o.answered),
                unanswered: asNumber(o.unanswered),
                totalTalkSeconds: asNumber(o.totalTalkSeconds ?? o.talkTimeSecondsSum),
                totalHoldSeconds: asNumber(o.totalHoldSeconds ?? o.holdTimeSecondsSum),
            };
        }
        return null;
    })
        .filter((row) => row !== null);
}
/**
 * `/api/qstat/queue-reports` queuePerformance tuple:
 * `[queue, answerLevel%, totalReceived, avgWait, totalWrapped, avgTalk, avgHold, avgSpeedOfAnswer, avgAbandoned, avgAcw]`
 */
function normalizeQueuePerformance(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (Array.isArray(row)) {
            const queueName = asString(row[0]);
            if (!queueName)
                return null;
            return {
                queueName,
                answerLevelPercent: asNumberOrNull(row[1]),
                totalReceivedCalls: asNumber(row[2]),
                avgWaitTime: asNumberOrNull(row[3]),
                totalWrappedCalls: asNumber(row[4]),
                avgTalkTime: asNumberOrNull(row[5]),
                avgHoldDuration: asNumberOrNull(row[6]),
                avgSpeedOfAnswer: asNumberOrNull(row[7]),
                avgAbandonedTime: asNumberOrNull(row[8]),
                avgAcwDuration: asNumberOrNull(row[9]),
            };
        }
        if (row && typeof row === 'object') {
            const o = row;
            const queueName = asString(o.queueName);
            if (!queueName)
                return null;
            return {
                queueName,
                answerLevelPercent: asNumberOrNull(o.answerLevelPercent),
                totalReceivedCalls: asNumber(o.totalReceivedCalls),
                avgWaitTime: asNumberOrNull(o.avgWaitTime ?? o.avgWaitTimeSeconds),
                totalWrappedCalls: asNumber(o.totalWrappedCalls),
                avgTalkTime: asNumberOrNull(o.avgTalkTime ?? o.avgTalkTimeSeconds),
                avgHoldDuration: asNumberOrNull(o.avgHoldDuration ?? o.avgHoldDurationSeconds),
                avgSpeedOfAnswer: asNumberOrNull(o.avgSpeedOfAnswer ?? o.avgSpeedOfAnswerPercent),
                avgAbandonedTime: asNumberOrNull(o.avgAbandonedTime ?? o.avgAbandonedTimeSeconds),
                avgAcwDuration: asNumberOrNull(o.avgAcwDuration ?? o.avgAcwDurationSeconds),
            };
        }
        return null;
    })
        .filter((row) => row !== null);
}
function normalizeDetailedSatisfaction(rows) {
    if (!Array.isArray(rows))
        return [];
    return rows
        .map((row) => {
        if (!row || typeof row !== 'object')
            return null;
        const o = row;
        const rating = asNumber(o.rating);
        return {
            callDateTime: asString(o.callDateTime ?? o.callTime),
            phone: asString(o.phone ?? o.caller),
            queueName: asString(o.queueName),
            agent: asString(o.agent),
            rating,
            feedbackInput: asString(o.feedbackInput) || (rating ? getRatingDisplay(rating) : ''),
        };
    })
        .filter((row) => row !== null && Boolean(row.callDateTime));
}
function normalizeSatisfaction(data) {
    const nested = data.satisfaction;
    const source = nested && typeof nested === 'object' ? nested : data;
    const detailedRaw = source.detailedCallRecords ?? source.satisfactionCallDetails ?? data.satisfactionCallDetails;
    return {
        totalRatedCalls: asNumber(source.totalRatedCalls ?? data.totalRatedCalls),
        overallAverageRating: asNumber(source.overallAverageRating ?? source.overallSatisfactionRate ?? data.overallSatisfactionRate),
        satisfactionByAgent: Array.isArray(source.satisfactionByAgent)
            ? source.satisfactionByAgent
            : Array.isArray(data.satisfactionByAgent)
                ? data.satisfactionByAgent
                : [],
        ratingDistribution: Array.isArray(source.ratingDistribution)
            ? source.ratingDistribution
            : Array.isArray(data.ratingDistribution)
                ? data.ratingDistribution
                : [],
        detailedCallRecords: normalizeDetailedSatisfaction(detailedRaw),
    };
}
/** Map `/api/qstat/queue-reports` tuple arrays to UI-friendly report objects. */
export function normalizeQueueStatusReport(raw) {
    const data = raw && typeof raw === 'object' ? raw : {};
    return {
        totalAnsweredCalls: asNumber(data.totalAnsweredCalls),
        totalUnansweredCalls: asNumber(data.totalUnansweredCalls),
        answeredByAgent: normalizeCountRows(data.answeredByAgent),
        answeredByQueue: normalizeCountRows(data.answeredByQueue),
        disconnectionCause: normalizeCountRows(data.disconnectionCause),
        unansweredByQueue: normalizeCountRows(data.unansweredByQueue),
        distPerDay: normalizeDistPerDay(data.distributionPerDay ?? data.distPerDay),
        distPerHour: normalizeDistPerHour(data.distributionPerHour ?? data.distPerHour),
        distPerDayOfWeek: normalizeDistPerDayOfWeek(data.distributionPerDayOfWeek ?? data.distPerDayOfWeek),
        queuePerformance: normalizeQueuePerformance(data.queuePerformance),
        satisfaction: normalizeSatisfaction(data),
    };
}
export function formatQueuePerformanceMetric(value) {
    if (value == null)
        return '';
    return value.toFixed(2);
}
/** Build queue/agent filter options from qstat dashboard + phone-users APIs. */
export function normalizeQueueStatusReference(dashboardRaw, phoneUsersRaw) {
    const analytics = normalizeQstatDashboardAnalytics(dashboardRaw);
    const queues = uniqueRefOptions(analytics.queues.map((q) => q.name));
    const phoneUsers = normalizePhoneUsersResponse(phoneUsersRaw).users;
    const agentNames = phoneUsers
        .map((u) => u.username?.trim() || u.phoneUser?.trim() || u.name?.trim())
        .filter(Boolean);
    const agents = uniqueRefOptions(agentNames);
    return { queues, agents };
}
